import {LocalDate} from '@js-joda/core';
import {useSnackbar} from 'notistack';
import React, {createContext, Dispatch, ReactNode, useCallback, useContext, useMemo} from 'react';
import useApiFetch, {apiEndpoint} from '../../Auth/Api';
import {Progress} from '../../LoadingIndicator/ProgressBackdrop';
import {useProjects} from '../../Projects/ProjectsProvider';
import {compileSchedules, Employee, EmployeeSchedule, Project, ProjectScheduleWeek} from './EmployeeSchedulesCompiler';
import {EmployeeSchedulesAction} from './EmployeeSchedulesReducer';
import {ReasonsAction} from './ReasonsReducer';

export type ProgressHandler = (progress : Progress) => void;

export type ScheduleContext = {
    activeProjects : Project[];
    projectsWithAllStatuses : Project[];
    employees : Employee[];
    multipleEmployeeSchedules : boolean;
    updateLock : (employeeId : number, projectId : number, locked : boolean) => Promise<void>;
    updateReason : (employeeId : number, startOfWeek : string, reason : string) => Promise<void>;
    refresh : (employee : Employee, startDate : LocalDate) => Promise<void>;
    updateSchedule : (employeeId : number, projectId : number, startOfWeek : string, hours : number) => Promise<void>;
    fillRight : (
        employeeId : number,
        projectId : number,
        weeks : ProjectScheduleWeek[],
        onProgressUpdate : ProgressHandler,
    ) => Promise<void>;
    addProject : (employeeId : number, project : Project, startDate : LocalDate) => Promise<void>;
    removeProject : (employeeId : number, projectId : number) => Promise<void>;
    applyToAllVisible : (
        sourceEmployeeId : number,
        project : Project,
        startOfWeek : string,
        startDate : LocalDate,
        hours : number,
        onProgressUpdate ?: ProgressHandler,
    ) => Promise<void>;
    applyToEmployees : (
        sourceEmployeeId : number,
        project : Project,
        startOfWeek : string,
        startDate : LocalDate,
        hours : number,
        employeeSchedules : EmployeeSchedule[],
        onProgressUpdate ?: ProgressHandler,
    ) => Promise<void>;
};

const scheduleContext = createContext<ScheduleContext | null>(null);

type Props = {
    employees : Employee[];
    dispatchEmployeeSchedules : Dispatch<EmployeeSchedulesAction>;
    dispatchReasons : Dispatch<ReasonsAction>;
    children : ReactNode;
    visibleEmployeeSchedules ?: EmployeeSchedule[];
};

const ScheduleContextProvider : React.FC<Props> = ({
    employees,
    dispatchEmployeeSchedules,
    dispatchReasons,
    children,
    visibleEmployeeSchedules,
} : Props) => {
    const apiFetch = useApiFetch();
    const {enqueueSnackbar} = useSnackbar();
    const {projectsWithAllStatuses, activeProjects} = useProjects();

    const updateLock = useCallback(async (employeeId : number, projectId : number, locked : boolean) => {
        const url = new URL(`/v1/scheduler/schedules/${projectId}/${employeeId}`, apiEndpoint);
        const response = await apiFetch(url.toString(), {
            method: 'PATCH',
            body: JSON.stringify({locked}),
        });

        if (!response.ok) {
            enqueueSnackbar('Could not update lock', {variant: 'error'});
            return;
        }

        dispatchEmployeeSchedules({type: 'updateLock', payload: {employeeId, projectId, locked}});
    }, [apiFetch, dispatchEmployeeSchedules, enqueueSnackbar]);

    const updateReason = useCallback(async (employeeId : number, startOfWeek : string, reason : string) => {
        const url = new URL(`/v1/scheduler/reasons/${employeeId}/${startOfWeek}`, apiEndpoint);
        const response = await apiFetch(url.toString(), {
            method: 'PUT',
            body: JSON.stringify({reason}),
        });

        if (!response.ok) {
            enqueueSnackbar('Could not update reason', {variant: 'error'});
            return;
        }

        dispatchReasons({type: 'update', payload: {employeeId, startOfWeek, reason}});
    }, [apiFetch, dispatchReasons, enqueueSnackbar]);

    const refresh = useCallback(async (employee : Employee, startDate : LocalDate) => {
        const [reasonsResponse, employeeActualsResponse, schedulesResponse] = await Promise.all([
            (async () => {
                const url = new URL('/v1/scheduler/reasons', apiEndpoint);
                url.searchParams.set('employeeId', employee.id.toString());

                return apiFetch(url.toString());
            })(),
            (async () => {
                const url = new URL('/v1/scheduler/actuals', apiEndpoint);
                url.searchParams.set('dateStart', startDate.toString());
                url.searchParams.set('dateEnd', startDate.plusWeeks(7).toString());
                url.searchParams.set('employeeId', employee.id.toString());

                return apiFetch(url.toString());
            })(),
            (async () => {
                const url = new URL('/v1/scheduler/schedules', apiEndpoint);
                url.searchParams.set('date', startDate.toString());
                url.searchParams.set('employeeId', employee.id.toString());

                return apiFetch(url.toString());
            })(),
        ]);

        if (!reasonsResponse.ok || !employeeActualsResponse.ok || !schedulesResponse.ok) {
            enqueueSnackbar('Could not refresh employee', {variant: 'error'});
            return;
        }

        const [reasons, employeeActuals, schedules] = await Promise.all([
            reasonsResponse.json(),
            employeeActualsResponse.json(),
            schedulesResponse.json(),
        ]);

        if (!projectsWithAllStatuses) {
            return;
        }

        const employeeSchedules = compileSchedules(startDate, [employee], projectsWithAllStatuses, schedules, employeeActuals);

        dispatchEmployeeSchedules({type: 'refresh', payload: {employeeSchedule: employeeSchedules[0]}});
        dispatchReasons({type: 'refresh', payload: {employeeId: employee.id, reasons}});
    }, [apiFetch, dispatchEmployeeSchedules, dispatchReasons, projectsWithAllStatuses, enqueueSnackbar]);

    const updateSchedule = useCallback(async (
        employeeId : number,
        projectId : number,
        startOfWeek : string,
        hours : number,
    ) => {
        const url = new URL(`/v1/scheduler/schedules/${projectId}/${employeeId}/${startOfWeek}`, apiEndpoint);
        const response = await apiFetch(url.toString(), {
            method: 'PUT',
            body: JSON.stringify({hours}),
        });

        if (!response.ok) {
            enqueueSnackbar('Could not update schedule', {variant: 'error'});
            return;
        }

        dispatchEmployeeSchedules({type: 'updateSchedule', payload: {employeeId, projectId, startOfWeek, hours}});
    }, [apiFetch, dispatchEmployeeSchedules, enqueueSnackbar]);

    const fillRight = useCallback(async (
        employeeId : number,
        projectId : number,
        weeks : ProjectScheduleWeek[],
        onProgressUpdate ?: ProgressHandler,
    ) => {
        let done = 0;
        onProgressUpdate && onProgressUpdate({current: 0, total: weeks.length});

        const results = await Promise.all(weeks.map(async week => {
            const url = new URL(`/v1/scheduler/schedules/${projectId}/${employeeId}/${week.startOfWeek}`, apiEndpoint);
            const response = await apiFetch(url.toString(), {
                method: 'PUT',
                body: JSON.stringify({hours: week.scheduled}),
            });

            onProgressUpdate && onProgressUpdate({current: ++done, total: weeks.length});
            return response.ok;
        }));

        if (results.some(result => !result)) {
            enqueueSnackbar('Could not fill one or more values', {variant: 'error'});
            return;
        }

        dispatchEmployeeSchedules({type: 'fillSchedule', payload: {employeeId, projectId, weeks}});
    }, [apiFetch, dispatchEmployeeSchedules, enqueueSnackbar]);

    const addProject = useCallback(async (employeeId : number, project : Project, startDate : LocalDate) => {
        const url = new URL(`/v1/scheduler/schedules/${project.id}/${employeeId}/${startDate.toString()}`, apiEndpoint);
        const response = await apiFetch(url.toString(), {method: 'POST'});

        if (!response.ok) {
            enqueueSnackbar('Could not add project', {variant: 'error'});
            return;
        }

        dispatchEmployeeSchedules({type: 'addProject', payload: {employeeId, project, startDate}});
    }, [apiFetch, dispatchEmployeeSchedules, enqueueSnackbar]);

    const removeProject = useCallback(async (employeeId : number, projectId : number) => {
        const url = new URL(`/v1/scheduler/schedules/${projectId}/${employeeId}`, apiEndpoint);
        const response = await apiFetch(url.toString(), {method: 'DELETE'});

        if (!response.ok) {
            enqueueSnackbar('Could not remove project', {variant: 'error'});
            return;
        }

        const {deletedFrom} = await response.json();
        dispatchEmployeeSchedules({type: 'removeProject', payload: {employeeId, projectId, deletedFrom}});
    }, [apiFetch, dispatchEmployeeSchedules, enqueueSnackbar]);

    const applyToEmployees = useCallback(async (
        sourceEmployeeId : number,
        project : Project,
        startOfWeek : string,
        startDate : LocalDate,
        hours : number,
        employeeSchedules : EmployeeSchedule[],
        onProgressUpdate ?: ProgressHandler,
    ) => {
        if (!visibleEmployeeSchedules) {
            return;
        }

        const schedulesToUpdate = employeeSchedules.filter(
            employeeSchedule => employeeSchedule.employee.id !== sourceEmployeeId
        );

        let done = 0;
        onProgressUpdate && onProgressUpdate({current: 0, total: schedulesToUpdate.length});

        const results = await Promise.all(schedulesToUpdate.map(async employeeSchedule => {
            const hasProject = employeeSchedule.schedules.some(schedule => schedule.project.id === project.id);

            if (!hasProject) {
                await apiFetch(
                    new URL(
                        `/v1/scheduler/schedules/${project.id}/${employeeSchedule.employee.id}/${startDate.toString()}`,
                        apiEndpoint
                    ).toString(),
                    {method: 'POST'}
                );
            }

            const response = await apiFetch(
                new URL(
                    `/v1/scheduler/schedules/${project.id}/${employeeSchedule.employee.id}/${startOfWeek}`,
                    apiEndpoint
                ).toString(),
                {method: 'PUT', body: JSON.stringify({hours})}
            );

            onProgressUpdate && onProgressUpdate({current: ++done, total: schedulesToUpdate.length});

            return response.ok;
        }));

        if (results.some(result => !result)) {
            enqueueSnackbar('Could not apply one or more values', {variant: 'error'});
            return;
        }

        dispatchEmployeeSchedules({type: 'applyToAllVisible', payload: {
            employeeIds: schedulesToUpdate.map(employeeSchedule => employeeSchedule.employee.id),
            startDate,
            project,
            hours,
            startOfWeek,
        }});
    }, [apiFetch, dispatchEmployeeSchedules, visibleEmployeeSchedules, enqueueSnackbar]);

    const applyToAllVisible = useCallback(async (
        sourceEmployeeId : number,
        project : Project,
        startOfWeek : string,
        startDate : LocalDate,
        hours : number,
        onProgressUpdate ?: ProgressHandler,
    ) => {
        if (!visibleEmployeeSchedules) {
            return;
        }

        const schedulesToUpdate = visibleEmployeeSchedules.filter(
            employeeSchedule => employeeSchedule.employee.id !== sourceEmployeeId
        );
        await applyToEmployees(
            sourceEmployeeId,
            project,
            startOfWeek,
            startDate,
            hours,
            schedulesToUpdate,
            onProgressUpdate,
        );
    }, [visibleEmployeeSchedules, applyToEmployees]);

    const value = useMemo(() => ({
        activeProjects: activeProjects || [],
        projectsWithAllStatuses: projectsWithAllStatuses || [],
        employees,
        multipleEmployeeSchedules: Boolean(visibleEmployeeSchedules && visibleEmployeeSchedules.length > 1),
        updateLock,
        updateReason,
        refresh,
        updateSchedule,
        fillRight,
        addProject,
        removeProject,
        applyToAllVisible,
        applyToEmployees,
    }), [
        activeProjects,
        projectsWithAllStatuses,
        employees,
        visibleEmployeeSchedules,
        updateLock,
        updateReason,
        refresh,
        updateSchedule,
        fillRight,
        addProject,
        removeProject,
        applyToAllVisible,
        applyToEmployees,
    ]);

    return (
        <scheduleContext.Provider value={value}>
            {children}
        </scheduleContext.Provider>
    );
};

export const useScheduleContext = () : ScheduleContext => {
    const context = useContext(scheduleContext);

    if (!context) {
        throw new Error('Schedule context used out of scope');
    }

    return context;
};

export default ScheduleContextProvider;
